/**
 * Web-side client for the Postgres-backed board listing (`GET /api/v2/boards`).
 *
 * Modeled on `membersApi.ts`: a pure, Jest-testable kernel that reuses the
 * caller's existing sign-in. The Vue layer supplies `idToken` (`getIdToken()`)
 * and `baseUrl`; the Boards page owns paging, sorting, search and filter state
 * and hands a `BoardsRequest` in. Postgres decides which boards exist and in
 * what order, already scoped to what the caller may read; every id in the
 * response is one the browser can then bind to Firestore for liveness.
 */

export type BoardsView = 'all' | 'mine' | 'shared_with_me' | 'everyone';
export type BoardsSort = 'created' | 'title' | 'description' | 'custom_field';
export type BoardsOrder = 'asc' | 'desc';

export interface BoardsRequest {
	// The account to read: the selected team, sent as `team_id` the way every
	// other api2 call names its account. Absent or null reads the caller's
	// personal boards.
	teamId?: string | null;
	// Which of the team's boards. Absent lets the endpoint choose: everyone's
	// for a holder of MANAGE_ALL_GENERATIONS, otherwise the caller's own, the
	// shared ones, and the restricted ones they are a member of.
	view?: BoardsView | null;
	// `next_cursor` from the previous page, or absent for the first. Opaque:
	// never build or parse one.
	cursor?: string | null;
	limit?: number;
	sort?: BoardsSort;
	order?: BoardsOrder;
	// The custom field `sort: 'custom_field'` orders by.
	customFieldKey?: string | null;
	q?: string | null;
	ownerUserIds?: string[] | null;
	// One access level (PRIVATE, SHARED or RESTRICTED), or absent for all of them.
	accessLevel?: string | null;
}

export interface BoardCustomFieldValue {
	key: string;
	label: string | null;
	value: string | null;
}

export interface BoardRow {
	id: string;
	// ISO 8601 in UTC, or null for a board with no creation time.
	created: string | null;
	title: string | null;
	description: string | null;
	accessLevel: string;
	ownerUserId: string | null;
	// The owner's email on the team. Null on a personal board, and once the
	// owner has left the team.
	ownerEmail: string | null;
	teamId: string | null;
	avatarUrl: string | null;
	// Every board custom field the company defines, in its order, with this
	// board's value.
	customFields: BoardCustomFieldValue[];
}

export interface BoardsPage {
	rows: BoardRow[];
	// Send back as `cursor` for the next page. Null on the last one.
	nextCursor: string | null;
	hasMore: boolean;
}

export interface BoardsFetchFailure {
	ok: false;
	reason: 'network' | 'unauthorized' | 'forbidden' | 'failed' | 'stale-cursor';
	// The server's structured error envelope ({ error: { code, message } }).
	code?: string;
	message?: string;
}

export type BoardsFetchResult =
	| { ok: true; page: BoardsPage }
	| BoardsFetchFailure;

/**
 * The failure, or null when the fetch worked. See `membersFetchFailure` for
 * why this is a function: `web` compiles with `strictNullChecks: false`, where
 * `if (!result.ok)` does not narrow the union.
 */
export function boardsFetchFailure(result: BoardsFetchResult): BoardsFetchFailure | null {
	return result.ok === true ? null : result;
}

export interface BoardsFetchInput {
	baseUrl: string;
	idToken: string;
	request: BoardsRequest;
	// Injectable so specs exercise the real request path without patching
	// the global; the page leaves it unset.
	fetchImpl?: typeof fetch;
}

/** Same origin the Library and members clients default to. */
export const BOARDS_API_BASE_URL: string =
	process.env.VUE_APP_API_BASE_URL || 'https://api2.rundiffusion.com/api/v2';

export const BOARDS_MIN_LIMIT = 1;
export const BOARDS_MAX_LIMIT = 100;
// The endpoint's cap on `q`. A longer value is a 400 for the whole request.
export const BOARDS_Q_MAX_LENGTH = 200;
// The endpoint's cap on owners in one filter.
export const BOARDS_OWNER_FILTER_MAX = 100;
export const BOARDS_DEFAULT_SORT: BoardsSort = 'created';

/** The endpoint's own default direction: newest first, A to Z for the rest. */
export function defaultBoardsOrder(sort: BoardsSort | null | undefined): BoardsOrder {
	return (sort || BOARDS_DEFAULT_SORT) === 'created' ? 'desc' : 'asc';
}

function present(value?: string | null): string | null {
	return value && value.trim() ? value.trim() : null;
}

/** Keep the page size inside the endpoint's accepted window. */
export function clampBoardsLimit(limit: number): number {
	const whole = Math.floor(Number(limit));
	if (!Number.isFinite(whole)) return BOARDS_MIN_LIMIT;
	return Math.min(BOARDS_MAX_LIMIT, Math.max(BOARDS_MIN_LIMIT, whole));
}

/**
 * The translation boundary between the page's camelCase state and the
 * endpoint's snake_case query string, so the URL carries only what deviates
 * from the defaults.
 */
export function buildBoardsQueryParams(request: BoardsRequest): URLSearchParams {
	const params = new URLSearchParams();

	const teamId = present(request.teamId);
	if (teamId) params.set('team_id', teamId);
	if (request.view) params.set('view', request.view);

	const cursor = present(request.cursor);
	if (cursor) params.set('cursor', cursor);
	if (request.limit !== undefined && request.limit !== null) {
		params.set('limit', String(clampBoardsLimit(request.limit)));
	}

	const sort: BoardsSort = request.sort || BOARDS_DEFAULT_SORT;
	const customFieldKey = present(request.customFieldKey);
	// A custom field sort without its field is a 400; the default sort is the
	// closest thing the endpoint will run.
	const effectiveSort: BoardsSort = sort === 'custom_field' && !customFieldKey ? BOARDS_DEFAULT_SORT : sort;
	if (effectiveSort !== BOARDS_DEFAULT_SORT) params.set('sort', effectiveSort);
	if (effectiveSort === 'custom_field') params.set('custom_field_key', customFieldKey);
	if (request.order && request.order !== defaultBoardsOrder(effectiveSort)) {
		params.set('order', request.order);
	}

	// Cut to the endpoint's cap: a search the server would refuse outright
	// becomes the closest search it will run.
	const q = present(request.q);
	if (q) params.set('q', q.slice(0, BOARDS_Q_MAX_LENGTH));

	const ownerUserIds: string[] = Array.from(new Set(
		(request.ownerUserIds || []).map((id: string) => present(id)).filter((id: string | null): id is string => !!id),
	)).slice(0, BOARDS_OWNER_FILTER_MAX);
	if (ownerUserIds.length) params.set('owner_user_id', ownerUserIds.join(','));
	const accessLevel = present(request.accessLevel);
	if (accessLevel) params.set('access_level', accessLevel);

	return params;
}

/** The request carries the bearer token and nothing else. */
export function authedHeaders(idToken: string): Record<string, string> {
	return {
		'Authorization': `Bearer ${idToken}`,
	};
}

function objectRecord(value: unknown): Record<string, unknown> | null {
	return value !== null && typeof value === 'object' && !Array.isArray(value)
		? value as Record<string, unknown>
		: null;
}

function stringOrNull(value: unknown): string | null {
	return typeof value === 'string' && value ? value : null;
}

function parseCustomField(candidate: unknown): BoardCustomFieldValue | null {
	const field = objectRecord(candidate);
	const key = field ? stringOrNull(field.key) : null;
	if (!field || !key) return null;
	return {
		key,
		label: stringOrNull(field.label),
		value: typeof field.value === 'string' ? field.value : null,
	};
}

function parseBoardRow(candidate: unknown): BoardRow | null {
	const row = objectRecord(candidate);
	const id = row ? stringOrNull(row.id) : null;
	if (!row || !id) return null;
	return {
		id,
		created: stringOrNull(row.created),
		title: stringOrNull(row.title),
		description: stringOrNull(row.description),
		accessLevel: stringOrNull(row.access_level) || 'PRIVATE',
		ownerUserId: stringOrNull(row.owner_user_id),
		ownerEmail: stringOrNull(row.owner_email),
		teamId: stringOrNull(row.team_id),
		avatarUrl: stringOrNull(row.avatar_url),
		customFields: Array.isArray(row.custom_fields)
			? row.custom_fields.map(parseCustomField).filter((f): f is BoardCustomFieldValue => f !== null)
			: [],
	};
}

/**
 * Normalize the wire envelope into the page's camelCase model, so the table
 * and its state helpers never see snake_case or a missing field.
 */
export function parseBoardsPage(body: unknown): BoardsPage {
	const envelope = objectRecord(body) || {};
	const rows = Array.isArray(envelope.data)
		? envelope.data.map(parseBoardRow).filter((r): r is BoardRow => r !== null)
		: [];
	return {
		rows,
		nextCursor: stringOrNull(envelope.next_cursor),
		hasMore: !!envelope.has_more,
	};
}

/** Best-effort read of `{ error: { code, message } }`. Never throws. */
async function readErrorEnvelope(resp: Response): Promise<{ code?: string; message?: string }> {
	try {
		const body: unknown = await resp.json();
		const err: Record<string, unknown> = objectRecord(objectRecord(body)?.error) || {};
		return {
			code: typeof err.code === 'string' ? err.code : undefined,
			message: typeof err.message === 'string' ? err.message : undefined,
		};
	} catch {
		return {};
	}
}

/**
 * One page of boards as a result union, so the page can branch on why a read
 * failed without ever catching.
 */
export async function fetchBoardsPage(input: BoardsFetchInput): Promise<BoardsFetchResult> {
	const qs = buildBoardsQueryParams(input.request).toString();
	const url = `${input.baseUrl.replace(/\/$/, '')}/boards${qs ? `?${qs}` : ''}`;
	const fetchImpl: typeof fetch = input.fetchImpl || window.fetch;

	let resp: Response;
	try {
		resp = await fetchImpl(url, {
			method: 'GET',
			headers: authedHeaders(input.idToken),
		});
	} catch {
		return { ok: false, reason: 'network' };
	}

	if (!resp.ok) {
		const { code, message } = await readErrorEnvelope(resp);
		// INVALID_CURSOR is its own outcome: the position we resumed from no
		// longer belongs to this listing, and the page starts the walk again.
		const reason: BoardsFetchFailure['reason'] = code === 'INVALID_CURSOR'
			? 'stale-cursor'
			: (resp.status === 401
				? 'unauthorized'
				: (resp.status === 403 ? 'forbidden' : 'failed'));
		return {
			ok: false,
			reason,
			...(code ? { code } : {}),
			message: message || `HTTP ${resp.status}`,
		};
	}

	let body: unknown;
	try {
		body = await resp.json();
	} catch {
		return { ok: false, reason: 'failed', message: 'Invalid JSON' };
	}

	return { ok: true, page: parseBoardsPage(body) };
}
